/**
 * useWebSearchHistory — Phase 7.5 (1.189.0).
 *
 * Per-tenant localStorage-persisted history of recent Web Search queries
 * for the Knowledge tab's KnowledgeWebSearchDetail panel. MRU-ordered,
 * dedup-on-append (case-insensitive trim), cap at MAX_HISTORY.
 *
 * Storage shape: JSON array of strings under
 *   `vibe_designer_web_search_history_<tenantId>`.
 * Single-tab assumption — cross-tab `storage` event sync is OUT OF SCOPE
 * (matches useShowSystemEvents Phase 6.3.1 precedent).
 *
 * Graceful localStorage failure (private mode, quota, serialization
 * error) silently falls back to in-memory state — the user's recent
 * queries for THIS session still work; only persistence is lost.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY_PREFIX = 'vibe_designer_web_search_history_';
const MAX_HISTORY = 10;

function storageKey(tenantId: string | null): string | null {
  if (!tenantId) return null;
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

function readInitial(tenantId: string | null): string[] {
  const key = storageKey(tenantId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop non-string entries defensively.
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_HISTORY);
  } catch {
    return [];
  }
}

function normalize(q: string): string | null {
  const trimmed = q.trim();
  if (!trimmed) return null;
  return trimmed;
}

function dedupAndCap(next: string, existing: string[]): string[] {
  const lower = next.toLowerCase();
  const filtered = existing.filter((q) => q.toLowerCase() !== lower);
  return [next, ...filtered].slice(0, MAX_HISTORY);
}

export interface UseWebSearchHistory {
  history: string[];
  append: (q: string) => void;
  clear: () => void;
}

export function useWebSearchHistory(tenantId: string | null): UseWebSearchHistory {
  const [history, setHistory] = useState<string[]>(() => readInitial(tenantId));
  // R2 #2 fix — track the tenant whose history is currently in state. When
  // tenantId changes, the re-hydrate runs FIRST (setting fresh state) and
  // the persist branch is skipped for the same render. Without this guard,
  // the persist effect could write the OLD tenant's history to the NEW
  // tenant's localStorage key during the re-render window before the
  // re-hydrate's setState commits. See `useWebSearchHistory.test.ts` →
  // `tenant-switch race` for the regression guard.
  const activeTenantRef = useRef<string | null>(tenantId);

  // Single effect: re-hydrate on tenant change, persist on history change.
  // Splitting these into two effects re-introduces the race documented above.
  useEffect(() => {
    if (activeTenantRef.current !== tenantId) {
      // Tenant changed — re-hydrate from the new tenant's storage. The
      // setHistory below schedules another render which will trigger this
      // effect again with activeTenantRef caught up; the persist branch
      // runs THEN.
      setHistory(readInitial(tenantId));
      activeTenantRef.current = tenantId;
      return;
    }
    // Same tenant — persist current history.
    const key = storageKey(tenantId);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(history));
    } catch {
      // Best-effort; in-memory state still correct.
    }
  }, [tenantId, history]);

  const append = useCallback(
    (q: string) => {
      const normalized = normalize(q);
      if (!normalized) return;
      setHistory((existing) => dedupAndCap(normalized, existing));
    },
    [],
  );

  const clear = useCallback(() => {
    setHistory([]);
  }, []);

  return { history, append, clear };
}

// Exported for tests.
export const _internals = {
  STORAGE_KEY_PREFIX,
  MAX_HISTORY,
  dedupAndCap,
  normalize,
};
